import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import DOMPurify from "dompurify";
import NewsService, { newsImageSrc } from "../services/news.service";
import { useTranslation } from "react-i18next";
import Spinner from "./Spinner";
import useSeo, { absoluteUrl, toPlainText } from "../seo/useSeo";

const NewsDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    NewsService.get(id)
      .then((res) => setItem(res.data))
      .catch((err) =>
        setError(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setLoading(false));
  }, [id]);

  useSeo(
    item
      ? {
          title: item.title,
          description: item.summary || toPlainText(item.content),
          image: item.image ? newsImageSrc(item.image) : undefined,
          type: "article",
          canonical: `/news/${item.id}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: item.title,
            description: item.summary || toPlainText(item.content),
            image: item.image ? [absoluteUrl(newsImageSrc(item.image))] : undefined,
            datePublished: item.createdAt,
            dateModified: item.updatedAt || item.createdAt,
            author: item.user ? { "@type": "Person", name: item.user.username } : undefined,
            mainEntityOfPage: absoluteUrl(`/news/${item.id}`),
          },
        }
      : error
      ? { title: t("news.title"), noindex: true }
      : null
  );

  if (loading) return <Spinner />;
  if (error) return <div className="container my-4"><div className="alert alert-danger">{error}</div></div>;
  if (!item) return null;

  return (
    <div className="container my-4">
      <Link to="/news" className="btn btn-sm btn-outline-secondary mb-3">
        ← {t("news.title")}
      </Link>
      <article>
        <h2>{item.title}</h2>
        <p className="text-muted">
          {new Date(item.createdAt).toLocaleString()}
          {item.user && <> · {item.user.username}</>}
        </p>
        {item.image && (
          <img src={newsImageSrc(item.image)} alt={item.title} className="img-fluid rounded mb-3" />
        )}
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(item.content || ""),
          }}
        />
      </article>
    </div>
  );
};

export default NewsDetail;
